import {
  AudioProgression,
  AudioProgressionPayload,
  ContentGUID,
  DocumentProgression,
  DocumentProgressionPayload,
  EntryComponentConfig,
  Envelope,
  PageProgression,
  TextProgressionPayload,
  VideoProgression,
  VideoProgressionPayload,
} from '../shared/types/common.model';
import { createQueryString } from '../shared/util/create-query-string';
import { DataService } from '../services/data_service';

/**
 * Learner Progression client abstract the interface between the backend API contract and the ViewModel structure.
 * It is data access layer on top of DataService, decoupling the UI components from the backend services.
 */
export class LearnerProgressionClient {
  private dataService: DataService;

  private readonly API_VERSION = '/api/v2';

  constructor(internalConfig: EntryComponentConfig) {
    this.dataService = DataService.getInstance(internalConfig.baseUrl);
  }

  /** Set learner progression for a given text module */
  setTextProgression(
    options: ContentGUID & {
      signal?: AbortSignal;
    }
  ) {
    const { id, courseId, moduleId, elementId, signal } = options;
    const payload: TextProgressionPayload = {
      data: {
        id,
        actions: 'loaded',
      },
      source: {
        type: 'course',
        id: courseId,
        child: {
          type: 'module',
          id: moduleId,
          child: {
            type: 'element',
            id: elementId,
          },
        },
      },
    };
    return this.dataService.post(`${this.API_VERSION}/learners/progressions/texts`, payload, {
      signal,
      keepalive: true,
    });
  }

  /** Set learner progression for a given audio module */
  setAudioProgression(
    options: ContentGUID & {
      durationInSeconds?: number;
      signal?: AbortSignal;
    }
  ) {
    const { id, courseId, moduleId, elementId, signal, durationInSeconds } = options;
    const payload: AudioProgressionPayload = {
      data: {
        id,
        action: 'loaded',
        ...(durationInSeconds != null && { 'duration-in-seconds': durationInSeconds }),
      },
      source: {
        type: 'course',
        id: courseId,
        child: {
          type: 'module',
          id: moduleId,
          child: {
            type: 'element',
            id: elementId,
          },
        },
      },
    };

    return this.dataService.post<Envelope<Array<AudioProgression>>>(
      `${this.API_VERSION}/learners/progressions/audios`,
      payload,
      {
        signal,
        keepalive: true,
      }
    );
  }

  /** Set learner progression for a given audio module */
  setDocumentProgression(
    options: ContentGUID & {
      pageNumber?: number;
      signal?: AbortSignal;
    }
  ) {
    const { id, courseId, moduleId, elementId, signal, pageNumber } = options;
    const payload: DocumentProgressionPayload = {
      data: {
        id,
        action: 'loaded',
        ...(pageNumber != null && { page_number: pageNumber }),
      },
      source: {
        type: 'course',
        id: courseId,
        child: {
          type: 'module',
          id: moduleId,
          child: {
            type: 'element',
            id: elementId,
          },
        },
      },
    };

    return this.dataService.post<Envelope<Array<DocumentProgression>>>(
      `${this.API_VERSION}/learners/progressions/documents`,
      payload,
      {
        signal,
        keepalive: true,
      }
    );
  }

  /** Set learner progression for a given video module */
  setVideoProgression(
    options: ContentGUID & {
      durationInSeconds?: number;
      signal?: AbortSignal;
    }
  ) {
    const { id, courseId, moduleId, elementId, signal, durationInSeconds } = options;
    const payload: VideoProgressionPayload = {
      data: {
        id,
        action: 'loaded',
        ...(durationInSeconds != null && { 'duration-in-seconds': durationInSeconds }),
      },
      source: {
        type: 'course',
        id: courseId,
        child: {
          type: 'module',
          id: moduleId,
          child: {
            type: 'element',
            id: elementId,
          },
        },
      },
    };

    return this.dataService.post<Envelope<Array<VideoProgression>>>(
      `${this.API_VERSION}/learners/progressions/videos`,
      payload,
      {
        signal,
        keepalive: true,
      }
    );
  }

  /** Fetch progression for a page module */
  async getPageProgression(
    options: { id: string; courseId: string } & {
      /** Signal object used to abort requests */
      signal?: AbortSignal;
    }
  ) {
    const { signal, ...parameters } = options;

    const response = await this.dataService.get<Envelope<Array<PageProgression>>>(
      `${this.API_VERSION}/learners/progressions/pages?${createQueryString(parameters)}`,
      signal
    );

    return response.data[0];
  }
}
